import { formatCurrency } from "./utils";

/** Regras puras da tela de repasse fora da plataforma (spec 2026-10-03 §6). */
export type AmbienteRepasse = "producao" | "sandbox" | "simulado";

export const formatarCentavos = (cents: number): string => formatCurrency(cents / 100);

/**
 * Texto em reais para centavos: aceita "1.234,56", "10,5", "10.50" e "R$ 150,00".
 * Sem vírgula, o ponto é decimal (até 2 casas): "1.234" é ambíguo e vira null.
 */
export function reaisParaCentavos(texto: string): number | null {
  const t = texto.trim().replace(/^R\$\s*/i, "");
  if (!t) return null;
  const normal = t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t;
  if (!/^\d+(\.\d{1,2})?$/.test(normal)) return null;
  return Math.round(Number(normal) * 100);
}

/** Parcial válido: maior que zero e MENOR que o disponível (igual ao disponível é o integral). */
export function erroValorParcial(valorCents: number | null, disponivelCents: number): string | null {
  if (valorCents === null) return "Informe um valor válido (ex.: 150,00).";
  if (valorCents <= 0) return "O valor deve ser maior que zero.";
  if (valorCents >= disponivelCents) {
    return `O valor deve ser menor que o disponível (${formatarCentavos(disponivelCents)}). Para quitar tudo, use "Valor integral".`;
  }
  return null;
}

export interface CorpoRegistroExterno {
  vistoriadoraId: string;
  ambiente: AmbienteRepasse;
  modalidade: "integral" | "parcial";
  valorCents?: number;
  observacao?: string;
  chaveIdempotencia: string;
  comprovanteId: string;
}

/** Integral nunca envia valor (o servidor usa o disponível); observação vazia não vai no corpo. */
export function corpoRegistroExterno(i: {
  vistoriadoraId: string; ambiente: AmbienteRepasse; modalidade: "integral" | "parcial";
  valorCents: number | null; observacao: string; chaveIdempotencia: string; comprovanteId: string;
}): CorpoRegistroExterno {
  const obs = i.observacao.trim();
  return {
    vistoriadoraId: i.vistoriadoraId,
    ambiente: i.ambiente,
    modalidade: i.modalidade,
    ...(i.modalidade === "parcial" && i.valorCents !== null ? { valorCents: i.valorCents } : {}),
    ...(obs ? { observacao: obs } : {}),
    chaveIdempotencia: i.chaveIdempotencia,
    comprovanteId: i.comprovanteId,
  };
}

export function erroArquivoComprovante(file: Pick<File, "name" | "type" | "size"> | null): string | null {
  if (!file) return "Anexe o comprovante para confirmar o repasse.";
  const extensao = file.name.split(".").pop()?.toLowerCase();
  const tipoValido = (file.type === "application/pdf" && extensao === "pdf") ||
    (file.type === "image/png" && extensao === "png") ||
    (file.type === "image/jpeg" && (extensao === "jpg" || extensao === "jpeg"));
  if (!tipoValido) return "Escolha um comprovante em PDF, JPG ou PNG.";
  if (file.size === 0 || file.size > 5 * 1024 * 1024) return "O arquivo deve ter conteúdo e no máximo 5 MB.";
  return null;
}

/** Rótulo do extrato: o sinal distingue o débito do estorno (mesma origem). */
export function rotuloOrigemLedger(origem: string, valorCents: number): string {
  if (origem === "repasse_externo") return valorCents < 0 ? "Repasse fora da plataforma" : "Repasse fora desfeito";
  return origem.replaceAll("_", " ");
}

/** O apiRequest lança "STATUS: corpo"; extrai { message, code } do JSON do servidor quando houver. */
export function erroDaApi(e: unknown): { message: string; code: string | undefined } {
  const bruto = e instanceof Error ? e.message : String(e);
  const m = bruto.match(/^\d{3}: ([\s\S]*)$/);
  if (!m) return { message: bruto, code: undefined };
  try {
    const j = JSON.parse(m[1]);
    if (j && typeof j.message === "string") return { message: j.message, code: typeof j.code === "string" ? j.code : undefined };
  } catch {
    // corpo não-JSON (ex.: página de erro do proxy): usa o texto como veio
  }
  return { message: m[1], code: undefined };
}
